import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container, Grid, Segment, Icon, Button, Form, Dropdown, Modal } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import useProgrammingService from "../../services/backoffice/hooks/useProgrammingService";
import { CreateProgrammingRequest, ProgrammingFilter, statusOptions, travelTypeOptions, UpdateProgrammingRequest } from "../../models/programming";
import { ProgrammingLocationDto } from "../../models/programming-location-dto";
import GoogleGeofenceMap from "../../components/torres/geofence-components/GoogleGeofenceMap";
import ProgrammingList from "../../components/programming-list-page/ProgrammingList";
import ProgrammingForm from "../backoffice/programming/components/ProgrammingForm";
import { ShortDto } from "../../models/short-dto";
import useVehiclePlateService from "../../services/backoffice/hooks/useVehiclePlateService";
import useClientService from "../../services/backoffice/hooks/useClientService";
import useReferenceService from "../../services/backoffice/hooks/useReferenceService";
import useProductsService from "../../services/backoffice/hooks/useProductsService";
import useDriverService from "../../services/backoffice/hooks/useDriverService";
import useRouteService from "../../services/backoffice/hooks/useRouteService";
import { useRankingService } from "../../services/backoffice/hooks/useRankingService";
import SessionStorage from "../../store/session";
import moment from 'moment';
import ProgrammingLocationRefreshButton from "../../components/programming-location-refresh-button";
import { isStatusInGroup, TorreStatusGroup } from "../../shared/utils/torre-tracking-utils";
import { readTorreListMode, TorreListMode, writeTorreListMode } from "../../shared/utils/torre-list-mode";
import TorreListModeToggle from "../../components/programming-list-page/TorreListModeToggle";
import { resolveSpreadsheetInlineEditAccess, SPREADSHEET_PAGE_SIZE } from "../../components/programming-list-page/programming-list-spreadsheet";
import {
    DEFAULT_TORRE_TERMINAL_LOOKBACK_DAYS,
    TORRE_OPERATIONAL_ORDER_BY,
    TORRE_WINDOW_MODE_HOJE,
    TORRE_WINDOW_MODE_PERIODO,
} from "../../components/programming-list-page/torre-operational-groups";
import { ToasterConfig } from "../../config/ToasterConfig";



const Torre: React.FC = () => {
    const { getProgrammingLocations, getProgrammingTorreSummary, createProgramming } = useProgrammingService();
    const { getAllVehiclePlatesShortDto } = useVehiclePlateService();
    const { getAllClientsShortDto } = useClientService();
    const { getReferencesShort } = useReferenceService();
    const productsService = useProductsService();
    const driverService = useDriverService();
    const routeService = useRouteService();
    const { getUserClient } = useRankingService();
    const [programmingCounts, setProgrammingCounts] = useState({
        aguardando: 0,      // Waiting, Created, Available
        emExecucao: 0,      // Loading, LoadedTransit, Unloading, InProgress, EmptyTransit
        atrasadas: 0,       // Todos os Delayed*
        finalizadas: 0,     // Completed, Finished
        canceladas: 0,      // Cancelled
    });
    const [selectedProgrammingId, setSelectedProgrammingId] = useState<string | null>(null);
    const [locations, setLocations] = useState<ProgrammingLocationDto[]>([]);
    const [showFilters, setShowFilters] = useState(false);
    const [filter, setFilter] = useState<ProgrammingFilter>({
        onlyWithLocation: false,
        hasCurrentLocation: false,
        page: 1,
        perPage: 500,
        orderBy: TORRE_OPERATIONAL_ORDER_BY,
        torreWindowMode: TORRE_WINDOW_MODE_HOJE,
        torreTerminalLookbackDays: DEFAULT_TORRE_TERMINAL_LOOKBACK_DAYS,
        includeTorreCancelled: false,
    });
    const [vehiclePlates, setVehiclePlates] = useState<ShortDto[]>([]);
    const [clients, setClients] = useState<ShortDto[]>([]);
    const [references, setReferences] = useState<ShortDto[]>([]);
    const [markerCount, setMarkerCount] = useState(0);
    const [statusGroupFilter, setStatusGroupFilter] = useState<TorreStatusGroup>('todos');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState<string>();
    const [formClients, setFormClients] = useState<ShortDto[]>([]);
    const [formVehiclePlates, setFormVehiclePlates] = useState<ShortDto[]>([]);
    const [formProducts, setFormProducts] = useState<ShortDto[]>([]);
    const [formDrivers, setFormDrivers] = useState<ShortDto[]>([]);
    const [formReferences, setFormReferences] = useState<ShortDto[]>([]);
    const [formRoutes, setFormRoutes] = useState<ShortDto[]>([]);
    const [lockedClientId, setLockedClientId] = useState<string>();
    const [isManager, setIsManager] = useState(false);
    const [canInlineEdit, setCanInlineEdit] = useState(false);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [listMode, setListMode] = useState<TorreListMode>(() => readTorreListMode());
    const [spreadsheetDrivers, setSpreadsheetDrivers] = useState<ShortDto[]>([]);
    const [spreadsheetProducts, setSpreadsheetProducts] = useState<ShortDto[]>([]);

    useEffect(() => {
        fetchVehiclePlates();
        fetchClients();
        fetchReferences();
        initializeSession();
        if (readTorreListMode() === 'spreadsheet') {
            fetchSpreadsheetLookups();
        }
    }, []);

    const initializeSession = async () => {
        const session = await SessionStorage.getSession();
        const profile = session?.userData?.profile;
        setIsManager(profile === 'Manager');

        let managerClientId: string | undefined;
        if (profile === 'Manager') {
            try {
                const userClient = await getUserClient();
                managerClientId = userClient?.clientId ?? undefined;
            } catch {
                managerClientId = undefined;
            }
        }

        const access = resolveSpreadsheetInlineEditAccess({
            profile,
            clientId: managerClientId,
        });
        setCanInlineEdit(access.canInlineEdit);
        setLockedClientId(access.lockedClientId);
    };

    const loadCreateFormData = async () => {
        try {
            setCreateLoading(true);
            const [clientsResponse, platesResponse, productsResponse, driversResponse, referencesResponse, routesResponse] = await Promise.all([
                getAllClientsShortDto(),
                getAllVehiclePlatesShortDto(),
                productsService.getProductsShort(),
                driverService.getAllDriversShortDto(),
                getReferencesShort(),
                routeService.getShortRoutes(),
            ]);
            setFormClients(clientsResponse || []);
            setFormVehiclePlates(platesResponse || []);
            setFormProducts(productsResponse || []);
            setFormDrivers(driversResponse || []);
            setFormReferences(referencesResponse || []);
            setFormRoutes(routesResponse || []);
        } catch (error: any) {
            toast.error('Erro ao carregar dados do formulário', ToasterConfig.error);
            setCreateError(error.message);
        } finally {
            setCreateLoading(false);
        }
    };

    const openCreateModal = async () => {
        setCreateError(undefined);
        setCreateModalOpen(true);
        await loadCreateFormData();
    };

    const handleCreateProgramming = async (formData: CreateProgrammingRequest | UpdateProgrammingRequest) => {
        try {
            setCreateLoading(true);
            await createProgramming({
                ...formData,
                clientId: lockedClientId || formData.clientId,
                isGoAndReturn: formData.isGoAndReturn ?? formData.isReturn ?? false,
            });
            toast.success('Programação criada com sucesso', ToasterConfig.success);
            setCreateModalOpen(false);
            await refreshTorreData({ reloadList: true });
        } catch (error: any) {
            setCreateError(error.message);
            toast.error('Erro ao criar programação', ToasterConfig.error);
        } finally {
            setCreateLoading(false);
        }
    };

    const fetchVehiclePlates = async () => {
        try {
            const response = await getAllVehiclePlatesShortDto();
            setVehiclePlates(response || []);
        } catch (error) {
        }
    };

    const fetchClients = async () => {
        try {
            const response = await getAllClientsShortDto();
            setClients(response || []);
        } catch (error) {
        }
    };

    const fetchReferences = async () => {
        try {
            const response = await getReferencesShort();
            setReferences(response || []);
        } catch (error) {
        }
    };

    const fetchSpreadsheetLookups = async () => {
        try {
            const [driversResponse, productsResponse] = await Promise.all([
                driverService.getAllDriversShortDto(),
                productsService.getProductsShort(),
            ]);
            setSpreadsheetDrivers(driversResponse || []);
            setSpreadsheetProducts(productsResponse || []);
        } catch {
            setSpreadsheetDrivers([]);
            setSpreadsheetProducts([]);
        }
    };

    const handleListModeChange = (mode: TorreListMode) => {
        setListMode(mode);
        writeTorreListMode(mode);
        setFilter(prev => ({
            ...prev,
            page: 1,
            perPage: mode === 'spreadsheet' ? SPREADSHEET_PAGE_SIZE : 500,
        }));
        if (mode === 'spreadsheet') {
            fetchSpreadsheetLookups();
        }
    };

    const fetchProgrammingCounts = async () => {
        try {
            const summary = await getProgrammingTorreSummary(filter);
            setProgrammingCounts({
                aguardando: summary?.aguardando ?? 0,
                emExecucao: summary?.emExecucao ?? 0,
                atrasadas: summary?.atrasadas ?? 0,
                finalizadas: summary?.finalizadas ?? 0,
                canceladas: summary?.canceladas ?? 0,
            });
        } catch {
            setProgrammingCounts({
                aguardando: 0,
                emExecucao: 0,
                atrasadas: 0,
                finalizadas: 0,
                canceladas: 0,
            });
        }
    };

    const fetchLocations = async () => {
        try {
            const response = await getProgrammingLocations({ ...filter, perPage: 0, page: 1 });
            const locationItems = Array.isArray(response) ? response : [];

            setLocations(locationItems);
        } catch (error: any) {
            if (error?.stack) {
            }
            setLocations([]);
        }
    };

    const refreshTorreData = async (options?: { reloadList?: boolean }) => {
        if (options?.reloadList) {
            setListRefreshKey(key => key + 1);
        }
        await Promise.all([fetchLocations(), fetchProgrammingCounts()]);
    };

    useEffect(() => {
        refreshTorreData();
        const interval = setInterval(refreshTorreData, 30000);
        return () => clearInterval(interval);
    }, [filter]);

    const handleProgrammingSelect = (programming: ProgrammingLocationDto) => {
        setSelectedProgrammingId(programming.id);
    };

    const selectedProgramming = useMemo(() => {
        if (!selectedProgrammingId) return null;
        return locations.find(location => location.id === selectedProgrammingId) ?? null;
    }, [selectedProgrammingId, locations]);

    const handleFilterChange = (name: string, value: any) => {
        setFilter(prev => ({ ...prev, [name]: value }));
    };


    const handleApplyFilters = () => {
        refreshTorreData();
    };

    const handleClearFilters = () => {
        setFilter({
            onlyWithLocation: false,
            hasCurrentLocation: false,
            page: 1,
            perPage: listMode === 'spreadsheet' ? SPREADSHEET_PAGE_SIZE : 500,
            orderBy: TORRE_OPERATIONAL_ORDER_BY,
            torreWindowMode: TORRE_WINDOW_MODE_HOJE,
            torreTerminalLookbackDays: DEFAULT_TORRE_TERMINAL_LOOKBACK_DAYS,
            includeTorreCancelled: false,
        });
        setStatusGroupFilter('todos');
        refreshTorreData();
    };

    const handleStatusFilter = (statusGroup: TorreStatusGroup) => {
        setStatusGroupFilter(statusGroup);
        setFilter(prev => ({
            ...prev,
            status: undefined,
            page: 1
        }));
    };

    const locationsByGroup = useMemo(() => {
        return locations.filter(location => isStatusInGroup(location.status, statusGroupFilter));
    }, [locations, statusGroupFilter]);

    return (
        <div style={{ margin: '0 -2%', width: '104%' }}>
        <Container fluid style={{ padding: '8px 12px', maxWidth: '100%' }}>
            <Segment raised style={{ margin: 0, padding: '16px', width: '100%' }}>
                <div>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column>
                                <Button
                                    color="green"
                                    icon={showFilters ? "filter" : "filter"}
                                    content={showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
                                    onClick={() => setShowFilters(!showFilters)}
                                />
                                <Button
                                    color="green"
                                    icon="plus"
                                    content="Nova programação"
                                    onClick={openCreateModal}
                                />
                                <ProgrammingLocationRefreshButton
                                    onRefreshComplete={() => refreshTorreData({ reloadList: true })}
                                    size="small"
                                />
                                <TorreListModeToggle
                                    mode={listMode}
                                    onChange={handleListModeChange}
                                />
                            </Grid.Column>
                        </Grid.Row>

                        {showFilters && (
                            <Grid.Row>
                                <Grid.Column>
                                    <Segment>
                                        <Form>
                                            <Form.Group widths="equal">
                                                <Form.Field>
                                                    <label>Cliente</label>
                                                    <Dropdown
                                                        placeholder="Nome do cliente"
                                                        fluid
                                                        selection
                                                        search
                                                        clearable
                                                        options={clients}
                                                        value={filter.clientId || ''}
                                                        onChange={(_, { value }) => handleFilterChange('clientId', value)}
                                                    />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label>Placa CM</label>
                                                    <Dropdown
                                                        placeholder="Placa principal"
                                                        fluid
                                                        selection
                                                        search
                                                        clearable
                                                        options={vehiclePlates}
                                                        value={filter.mainVehiclePlateId || ''}
                                                        onChange={(_, { value }) => handleFilterChange('mainVehiclePlateId', value)}
                                                    />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label>Status</label>
                                                    <Dropdown
                                                        placeholder="Selecione"
                                                        fluid
                                                        selection
                                                        options={statusOptions}
                                                        value={filter.status !== undefined ? filter.status : ''}
                                                        onChange={(_, { value }) => handleFilterChange('status', value)}
                                                    />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label>Tipo de Deslocamento</label>
                                                    <Dropdown
                                                        placeholder="Selecione"
                                                        fluid
                                                        selection
                                                        options={travelTypeOptions}
                                                        value={filter.travelType !== undefined ? filter.travelType : ''}
                                                        onChange={(_, { value }) => handleFilterChange('travelType', value)}
                                                    />
                                                </Form.Field>
                                            </Form.Group>

                                            <Form.Group widths="equal">
                                                <Form.Field>
                                                    <label>Origem</label>
                                                    <Dropdown
                                                        placeholder="Selecione a origem"
                                                        fluid
                                                        selection
                                                        search
                                                        clearable
                                                        options={references}
                                                        value={filter.originReferenceId || ''}
                                                        onChange={(_, { value }) => handleFilterChange('originReferenceId', value)}
                                                    />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label>Destino</label>
                                                    <Dropdown
                                                        placeholder="Selecione o destino"
                                                        fluid
                                                        selection
                                                        search
                                                        clearable
                                                        options={references}
                                                        value={filter.destinationReferenceId || ''}
                                                        onChange={(_, { value }) => handleFilterChange('destinationReferenceId', value)}
                                                    />
                                                </Form.Field>
                                                <Form.Field>
                                                    <label>Visão da lista</label>
                                                    <Button.Group fluid>
                                                        <Button
                                                            type="button"
                                                            active={filter.torreWindowMode !== TORRE_WINDOW_MODE_PERIODO}
                                                            onClick={() => handleFilterChange('torreWindowMode', TORRE_WINDOW_MODE_HOJE)}
                                                        >
                                                            Hoje
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            active={filter.torreWindowMode === TORRE_WINDOW_MODE_PERIODO}
                                                            onClick={() => handleFilterChange('torreWindowMode', TORRE_WINDOW_MODE_PERIODO)}
                                                        >
                                                            Período
                                                        </Button>
                                                    </Button.Group>
                                                </Form.Field>
                                                {filter.torreWindowMode === TORRE_WINDOW_MODE_HOJE ? (
                                                    <Form.Field>
                                                        <label>Histórico terminal (dias)</label>
                                                        <Form.Input
                                                            type="number"
                                                            min={0}
                                                            max={30}
                                                            value={filter.torreTerminalLookbackDays ?? DEFAULT_TORRE_TERMINAL_LOOKBACK_DAYS}
                                                            onChange={(_, { value }) => handleFilterChange(
                                                                'torreTerminalLookbackDays',
                                                                value === '' ? DEFAULT_TORRE_TERMINAL_LOOKBACK_DAYS : Number(value)
                                                            )}
                                                        />
                                                    </Form.Field>
                                                ) : (
                                                    <>
                                                        <Form.Field>
                                                            <label>Data Inicial</label>
                                                            <Form.Input
                                                                type="datetime-local"
                                                                value={filter.startDate ? moment(filter.startDate).format('YYYY-MM-DDTHH:mm') : ''}
                                                                onChange={(_, { value }) => handleFilterChange('startDate', value ? new Date(value) : null)}
                                                            />
                                                        </Form.Field>
                                                        <Form.Field>
                                                            <label>Data Final</label>
                                                            <Form.Input
                                                                type="datetime-local"
                                                                value={filter.endDate ? moment(filter.endDate).format('YYYY-MM-DDTHH:mm') : ''}
                                                                onChange={(_, { value }) => handleFilterChange('endDate', value ? new Date(value) : null)}
                                                            />
                                                        </Form.Field>
                                                    </>
                                                )}
                                            </Form.Group>

                                            <Form.Group>
                                                <Form.Field style={{ flex: 1 }}>
                                                    <label>Localização</label>
                                                    <Form.Group>
                                                        <Form.Checkbox
                                                            label="Apenas com Localização"
                                                            checked={filter.onlyWithLocation}
                                                            onChange={(_, { checked }) => handleFilterChange('onlyWithLocation', checked)}
                                                        />
                                                        <Form.Checkbox
                                                            label="Com Localização Atual"
                                                            checked={filter.hasCurrentLocation}
                                                            onChange={(_, { checked }) => handleFilterChange('hasCurrentLocation', checked)}
                                                        />
                                                    </Form.Group>
                                                </Form.Field>
                                                <Form.Field style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-5px' }}>
                                                    <Button.Group>
                                                        <Button
                                                            color="green"
                                                            icon="search"
                                                            content="Filtrar"
                                                            onClick={handleApplyFilters}
                                                        />
                                                        <Button
                                                            color="grey"
                                                            icon="eraser"
                                                            content="Limpar"
                                                            onClick={handleClearFilters}
                                                        />
                                                    </Button.Group>
                                                </Form.Field>
                                            </Form.Group>
                                        </Form>
                                    </Segment>
                                </Grid.Column>
                            </Grid.Row>
                        )}

                        <Grid.Row>
                            <Grid.Column>
                                <h1>Monitor de Etapas</h1>
                                <p style={{ marginTop: '-8px', color: '#6b7280' }}>
                                    {markerCount} veiculo(s) com localizacao visivel no mapa
                                </p>
                                <Grid columns={5} divided>
                                    <Grid.Column>
                                        <Card 
                                            style={{
                                                backgroundColor: '#3498db',
                                                color: 'white',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: statusGroupFilter === 'aguardando' ? '3px solid #1f2937' : 'none'
                                            }}
                                            onClick={() => handleStatusFilter('aguardando')}
                                        >
                                            <Card.Content>
                                                <Card.Header style={{ color: 'white', textAlign: 'center' }}>
                                                    <Icon name="pause" size="large" style={{ marginRight: '10px' }} />
                                                    Aguardando
                                                </Card.Header>
                                                <Card.Description style={{ color: 'white', textAlign: 'center', fontSize: '1.5em' }}>
                                                    {programmingCounts.aguardando}
                                                </Card.Description>
                                            </Card.Content>
                                        </Card>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <Card 
                                            style={{
                                                backgroundColor: '#f39c12',
                                                color: 'white',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: statusGroupFilter === 'emExecucao' ? '3px solid #1f2937' : 'none'
                                            }}
                                            onClick={() => handleStatusFilter('emExecucao')}
                                        >
                                            <Card.Content>
                                                <Card.Header style={{ color: 'white', textAlign: 'center' }}>
                                                    <Icon name="shipping fast" size="large" style={{ marginRight: '10px' }} />
                                                    Em Execução
                                                </Card.Header>
                                                <Card.Description style={{ color: 'white', textAlign: 'center', fontSize: '1.5em' }}>
                                                    {programmingCounts.emExecucao}
                                                </Card.Description>
                                            </Card.Content>
                                        </Card>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <Card 
                                            style={{
                                                backgroundColor: '#e74c3c',
                                                color: 'white',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: statusGroupFilter === 'atrasadas' ? '3px solid #1f2937' : 'none'
                                            }}
                                            onClick={() => handleStatusFilter('atrasadas')}
                                        >
                                            <Card.Content>
                                                <Card.Header style={{ color: 'white', textAlign: 'center' }}>
                                                    <Icon name="clock outline" size="large" style={{ marginRight: '10px' }} />
                                                    Atrasadas
                                                </Card.Header>
                                                <Card.Description style={{ color: 'white', textAlign: 'center', fontSize: '1.5em' }}>
                                                    {programmingCounts.atrasadas}
                                                </Card.Description>
                                            </Card.Content>
                                        </Card>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <Card 
                                            style={{
                                                backgroundColor: '#27ae60',
                                                color: 'white',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: statusGroupFilter === 'finalizadas' ? '3px solid #1f2937' : 'none'
                                            }}
                                            onClick={() => handleStatusFilter('finalizadas')}
                                        >
                                            <Card.Content>
                                                <Card.Header style={{ color: 'white', textAlign: 'center' }}>
                                                    <Icon name="checkmark" size="large" style={{ marginRight: '10px' }} />
                                                    Finalizadas
                                                </Card.Header>
                                                <Card.Description style={{ color: 'white', textAlign: 'center', fontSize: '1.5em' }}>
                                                    {programmingCounts.finalizadas}
                                                </Card.Description>
                                            </Card.Content>
                                        </Card>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <Card 
                                            style={{
                                                backgroundColor: '#95a5a6',
                                                color: 'white',
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: statusGroupFilter === 'canceladas' ? '3px solid #1f2937' : 'none'
                                            }}
                                            onClick={() => handleStatusFilter('canceladas')}
                                        >
                                            <Card.Content>
                                                <Card.Header style={{ color: 'white', textAlign: 'center' }}>
                                                    <Icon name="cancel" size="large" style={{ marginRight: '10px' }} />
                                                    Canceladas
                                                </Card.Header>
                                                <Card.Description style={{ color: 'white', textAlign: 'center', fontSize: '1.5em' }}>
                                                    {programmingCounts.canceladas}
                                                </Card.Description>
                                            </Card.Content>
                                        </Card>
                                    </Grid.Column>
                                </Grid>

                                <Grid stackable style={{ marginTop: '1em' }}>
                                    <Grid.Row>
                                        <Grid.Column width={16} style={{ position: 'relative', zIndex: 1 }}>
                                            <GoogleGeofenceMap
                                                mode="tracking"
                                                selectedProgramming={selectedProgramming}
                                                selectedProgrammingId={selectedProgrammingId}
                                                trackingLocations={locationsByGroup}
                                                onMarkerCountChange={setMarkerCount}
                                                onProgrammingSelect={handleProgrammingSelect}
                                                showTrackingLegend={true}
                                                mapHeight={listMode === 'spreadsheet' ? 'min(20vh, 180px)' : 'min(48vh, 520px)'}
                                            />
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row className="torre-list-below-map" style={{ marginTop: '1rem', position: 'relative', zIndex: 2 }}>
                                        <Grid.Column width={16}>
                                            <Segment
                                                style={{
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    borderRadius: '8px',
                                                    padding: '0.75rem',
                                                }}
                                            >
                                                <ProgrammingList
                                                    isCompact={true}
                                                    listMode={listMode}
                                                    tableUxVariant="enhanced"
                                                    showTorreTableToolbar
                                                    showDensityToggle
                                                    defaultOrderBy={TORRE_OPERATIONAL_ORDER_BY}
                                                    selectedProgrammingId={selectedProgrammingId}
                                                    canInlineEdit={canInlineEdit}
                                                    isManager={isManager}
                                                    lockedClientId={lockedClientId}
                                                    spreadsheetLookups={{
                                                        vehiclePlates,
                                                        clients,
                                                        drivers: spreadsheetDrivers,
                                                        products: spreadsheetProducts,
                                                        references,
                                                    }}
                                                    onSelect={handleProgrammingSelect}
                                                    showFilters={false}
                                                    filter={filter}
                                                    statusGroupFilter={statusGroupFilter}
                                                    refreshKey={listRefreshKey}
                                                    torreCancelledCount={programmingCounts.canceladas}
                                                    onFilterChange={(newFilter) => {
                                                        setFilter(prev => ({ ...prev, ...newFilter }));
                                                    }}
                                                />
                                            </Segment>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </div>
            </Segment>

            <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} size="large" closeIcon>
                <Modal.Header>Nova programação</Modal.Header>
                <Modal.Content scrolling>
                    <ProgrammingForm
                        variant="torre"
                        onSubmit={handleCreateProgramming}
                        onCancel={() => setCreateModalOpen(false)}
                        loading={createLoading}
                        error={createError}
                        clients={formClients}
                        vehiclePlates={formVehiclePlates}
                        products={formProducts}
                        drivers={formDrivers}
                        references={formReferences}
                        routes={formRoutes}
                        lockedClientId={lockedClientId}
                        clientDisabled={isManager}
                    />
                </Modal.Content>
            </Modal>
        </Container>
        </div>
    );
};

export default Torre;